import { booleanPointInPolygon, point, polygon } from '@turf/turf'
import {
  GeckoComponentCoords,
  GeckoComponentRect,
  GeckoMeasurementUnit,
  GeckoPolygonalPlate,
} from 'types'

export type PlateSelection =
  | GeckoComponentCoords
  | GeckoComponentRect
  | GeckoPolygonalPlate
  | undefined

const METERS = GeckoMeasurementUnit.Meters

function selectionPoint(
  selection: GeckoComponentCoords | GeckoComponentRect
): [number, number] {
  if (selection instanceof GeckoComponentCoords) {
    return [selection.x.toNumber(METERS), selection.y.toNumber(METERS)]
  }
  return [
    (selection.x.start.toNumber(METERS) + selection.x.end.toNumber(METERS)) / 2,
    (selection.y.start.toNumber(METERS) + selection.y.end.toNumber(METERS)) / 2,
  ]
}

function platePolygon(plate: GeckoPolygonalPlate) {
  const coordinates = plate.geometry.map(({ x, y }) => [
    x.toNumber(METERS),
    y.toNumber(METERS),
  ])
  const first = coordinates[0]
  const last = coordinates.at(-1)
  const closedCoordinates =
    first && last && first[0] === last[0] && first[1] === last[1]
      ? coordinates
      : [...coordinates, first]
  return polygon([closedCoordinates])
}

export function plateIdForSelection(
  selection: PlateSelection,
  plates: ReadonlyMap<string, GeckoPolygonalPlate>
): string | undefined {
  if (!selection) return undefined
  if (selection instanceof GeckoPolygonalPlate) {
    return plates.has(selection.id) ? selection.id : undefined
  }

  const selectedPoint = point(selectionPoint(selection))
  for (const plate of plates.values()) {
    if (selection.componentID !== plate.polygonUnits.componentID) continue
    if (booleanPointInPolygon(selectedPoint, platePolygon(plate))) {
      return plate.id
    }
  }
  return undefined
}
